import { useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Header, scrollToId } from "@/components/Header";
import { EnquiryForm } from "@/components/EnquiryForm";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, SITE_URL } from "@/hooks/useSeo";
import { SERVICES } from "@/data/services";
import { CHARTER_CHIPS } from "@/data/locations";

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const IMAGE_REVEAL = {
  initial: { clipPath: "inset(100% 0% 0% 0%)" },
  animate: { clipPath: "inset(0% 0% 0% 0%)" },
  transition: { duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] },
};

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.slug === slug);
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrapRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  useEffect(() => {
    if (!service) navigate("/", { replace: true });
  }, [service, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const AREA = [
    "Canberra",
    "Australian Capital Territory",
    "Sydney",
    "New South Wales",
    "Albury-Wodonga",
    "NSW South Coast",
  ];
  useSeo(
    service
      ? {
          title: service.seoTitle,
          description: service.seoDescription,
          path: `/services/${service.slug}`,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: service.title,
              name: service.title,
              description: service.seoDescription,
              url: `${SITE_URL}/services/${service.slug}`,
              areaServed: AREA,
              provider: {
                "@type": "FinancialService",
                name: "NUA Private Wealth",
                areaServed: AREA,
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: service.title,
                  item: `${SITE_URL}/services/${service.slug}`,
                },
              ],
            },
          ],
        }
      : {}
  );

  if (!service) return null;

  const others = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <>
      <Header />
      <main>
        <section data-testid="service-hero" className="relative overflow-hidden bg-[#F9F8F4] pt-16 md:pt-[88px]">
          <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
            <div className="md:col-span-7 pt-10 md:pt-20">
              <motion.img
                src="/assets/nua-mark-transparent.png"
                alt=""
                aria-hidden="true"
                data-testid="service-triskele"
                {...fade(0)}
                className="mb-8 h-10 md:h-12 w-auto opacity-90"
              />
              <motion.p
                data-testid="service-overline"
                {...fade(0.1)}
                className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]"
              >
                <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                Independent Financial Advice
              </motion.p>

              <h1
                data-testid="service-heading"
                className="mt-8 font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] text-[#111413]"
              >
                {service.title}
              </h1>

              <motion.p
                data-testid="service-subhead"
                {...fade(0.5)}
                className="mt-5 font-serif italic text-xl md:text-2xl text-[#0A2333]"
              >
                Fee-for-service advice in Canberra, Sydney, NSW and Australia wide.
              </motion.p>

              <motion.p
                data-testid="service-subcopy"
                {...fade(0.7)}
                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
              >
                {service.intro}
              </motion.p>

              <motion.div {...fade(0.85)} className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  data-testid="service-cta-book"
                  onClick={() => scrollToId("contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
                >
                  Book a free consultation
                  <ArrowUpRight size={15} />
                </button>
                <Link
                  to="/"
                  data-testid="service-cta-home"
                  className="text-xs uppercase tracking-[0.15em] text-[#111413] underline decoration-[#A78558] decoration-2 underline-offset-8 hover:text-[#0A2333] transition-colors duration-300"
                >
                  All services
                </Link>
              </motion.div>

              <motion.div {...fade(1.0)} className="mt-12 pb-16 flex flex-wrap gap-3" data-testid="service-chips">
                {CHARTER_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="border border-[#0A2333]/30 text-[#0A2333] text-[10px] uppercase tracking-[0.2em] px-4 py-2"
                  >
                    {chip}
                  </span>
                ))}
              </motion.div>
            </div>

            <div className="md:col-span-5 md:pt-12 pb-16 md:pb-24">
              <motion.div
                ref={imgWrapRef}
                {...IMAGE_REVEAL}
                className="relative overflow-hidden rounded-t-[10rem] h-[52vh] md:h-[70vh] border border-black/10"
                data-testid="service-image-frame"
              >
                <motion.img
                  src={service.image}
                  alt={service.imageAlt}
                  style={{ y }}
                  className="absolute inset-0 h-[120%] w-full object-cover"
                  data-testid="service-image"
                />
              </motion.div>
              <motion.p {...fade(1.15)} className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">
                {service.imageCaption}
              </motion.p>
            </div>
          </div>
        </section>

        <section data-testid="service-included" className="bg-[#EFEBE2] py-16 md:py-36">
          <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="md:sticky md:top-28">
                <Reveal>
                  <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                    <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                    What's involved
                  </p>
                  <h2 data-testid="service-included-heading" className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413]">
                    What {service.title.toLowerCase()} covers.
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-[#4A524D]">
                    {service.slug === "advice-for-busy-families" ? (
                      "Life gets busy, and essential financial tasks often take a back seat. Whether you need to organize your retirement strategy, update your estate plan, or structure your taxes, NUA Private Wealth streamlines the process into a single, comprehensive package. We take care of the heavy lifting so you can finally check these off your to-do list with peace of mind. Reach out today to see how we can assist."
                    ) : service.slug === "personal-insurance-advice" ? (
                      "NUA Private Wealth helps you find the right balance of Life, TPD, Trauma, and Income Protection coverage at a cost you can afford. Plus, because we are truly independent, 100% of insurance commissions are refunded to you."
                    ) : (
                      "Every engagement is scoped and priced before we begin — fixed fees, agreed in writing, with no commissions and no percentage of your assets."
                    )}
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="md:col-span-8">
              {service.included.map((item, i) => (
                <Reveal key={item.title} delay={i * 0.06}>
                  <div
                    data-testid={`service-point-${i + 1}`}
                    className="grid grid-cols-12 items-baseline gap-x-4 gap-y-2 border-t border-black/15 px-2 md:px-4 py-8 transition-[background-color] duration-300 hover:bg-[#F9F8F4]"
                  >
                    <span className="col-span-2 md:col-span-1 font-serif italic text-lg text-[#A78558]">
                      0{i + 1}
                    </span>
                    <h3 className="col-span-10 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight text-[#111413]">
                      {item.title}
                    </h3>
                    <p className="col-span-10 col-start-3 md:col-span-7 md:col-start-6 text-sm leading-relaxed text-[#4A524D]">
                      {item.body}
                    </p>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-black/15" aria-hidden="true" />
            </div>
          </div>
        </section>

        <EnquiryForm />

        <section data-testid="service-related" className="bg-[#F9F8F4] py-14 md:py-28 border-t border-black/10">
          <div className="mx-auto max-w-7xl px-6 md:px-10">
            <Reveal>
              <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                Explore more advice
              </p>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413]">
                Other ways we can help.
              </h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.04}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`related-service-${s.slug}`}
                    className="group flex items-center justify-between gap-4 border border-black/15 bg-white px-6 py-5 transition-[background-color,border-color,transform] duration-300 hover:bg-[#0A2333] hover:border-[#0A2333] hover:-translate-y-0.5"
                  >
                    <span className="font-serif text-lg md:text-xl tracking-tight text-[#111413] group-hover:text-[#F9F8F4] transition-colors duration-300">
                      {s.title}
                    </span>
                    <ArrowRight
                      size={18}
                      className="shrink-0 text-[#A78558] transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="service-closing" className="bg-[#0A2333] border-t border-white/10 py-16 md:py-20">
          <Reveal>
            <p className="text-center font-serif italic text-2xl md:text-4xl text-[#A78558] px-6">
              Wealth creates freedom for what's next.
            </p>
          </Reveal>
        </section>
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
